import { AxiosResponse } from 'axios';
import { BASE_URL } from '../config';
import axiosApiService from '@/services/axiosApiService';
import IProfile, { IProfilePublic } from '@/types/IProfile';
import IList, { IListPaging } from '@/types/IList';
import { IOrganisationSettings } from '@/types/IOrganisation';

const profilesAPI = {
  listProfiles: ({
    orgID = -1,
    page = 1,
    pageSize = 20,
    sort = 'date',
    order = 'desc',
    status = '',
    search = '',
  }: Partial<IList>): Promise<
    AxiosResponse<{ data: IProfile[]; paging: IListPaging }>
  > => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles?page_size=${pageSize}&page=${page}&sort[${sort}]=${order}${
        status !== '' ? `&status=${status}` : ''
      }${search !== '' ? `&search=${search}` : ''}`,
    );
  },
  listProfilesInGroup: ({
    orgID = -1,
    page = 1,
    pageSize = 20,
    sort = 'date',
    order = 'desc',
    status = '',
    search = '',
    groupID = 0,
  }: Partial<IList>): Promise<
    AxiosResponse<{ data: IProfile[]; paging: IListPaging }>
  > => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles?page_size=${pageSize}&page=${page}&sort[${sort}]=${order}${
        status !== '' ? `&status=${status}` : ''
      }${search !== '' ? `&search=${search}` : ''}${
        groupID !== null ? `&group_id=${groupID}` : ''
      }`,
    );
  },
  listProfilesNotInGroup: ({
    orgID = -1,
    page = 1,
    pageSize = 20,
    sort = 'date',
    order = 'desc',
    status = '',
    search = '',
    groupID = 0,
  }: Partial<IList>): Promise<
    AxiosResponse<{ data: IProfile[]; paging: IListPaging }>
  > => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles?page_size=${pageSize}&page=${page}&sort[${sort}]=${order}${
        status !== '' ? `&status=${status}` : ''
      }${search !== '' ? `&search=${search}` : ''}${
        groupID !== null ? `&not_group_id=${groupID}` : ''
      }`,
    );
  },
  createProfile: (
    orgID: number,
    body: {
      profile: Partial<IProfile>;
    },
  ): Promise<AxiosResponse<{ data: IProfile }>> => {
    return axiosApiService.post(
      `${BASE_URL}/api/organisations/${orgID}/profiles`,
      body,
    );
  },
  updateProfile: ({
    orgID,
    profileID,
    body,
  }: {
    orgID: number;
    profileID: number;
    body: {
      email_profile_editing?: boolean;
      profile: Partial<IProfile>;
    };
  }): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/${profileID}`,
      body,
    );
  },
  updateProfilesShared: ({
    orgID,
    body,
  }: {
    orgID: number;
    body: {
      ids: number[];
      profile: Partial<IProfile>;
    };
  }): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/shared`,
      body,
    );
  },
  showProfile: (
    orgID: number,
    profileID: number,
  ): Promise<AxiosResponse<{ data: IProfile }>> => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles/${profileID}`,
    );
  },
  showProfileWithHashID: (
    hashID: string,
  ): Promise<AxiosResponse<{ data: IProfilePublic }>> => {
    return axiosApiService.get(`${BASE_URL}/api/profiles/${hashID}`);
  },
  showProfilesShared: (
    orgID: number,
    ids: number[],
  ): Promise<AxiosResponse<{ data: IProfile }>> => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles/shared_2`,
      {
        params: { ids },
      },
    );
  },
  updateProfilesSharedNew: ({
    orgID,
    body,
  }: {
    orgID: number;
    body: {
      ids: number[];
      email_profile_editing?: boolean;
      profile: Partial<IProfile>;
    };
  }): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/shared_2`,
      body,
    );
  },
  updateProfileWithHashID: (
    hashID: string,
    body: {
      profile: Partial<IProfile>;
    },
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(`${BASE_URL}/api/profiles/${hashID}`, body);
  },
  deleteProfile: (
    orgID: number,
    profileID: number,
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.delete(
      `${BASE_URL}/api/organisations/${orgID}/profiles/${profileID}`,
    );
  },
  resetProfile: (
    orgID: number,
    profileID: number,
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/${profileID}/reset`,
    );
  },
  deleteProfiles: (
    orgID: number,
    profileIDs: number[],
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/delete_batch`,
      {
        profile_ids: profileIDs,
      },
    );
  },
  resetProfiles: (
    orgID: number,
    profileIDs: number[],
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.put(
      `${BASE_URL}/api/organisations/${orgID}/profiles/reset_batch`,
      {
        profile_ids: profileIDs,
      },
    );
  },
  importProfilesCSV: (
    orgID: number,
    formData: FormData,
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.post(
      `${BASE_URL}/api/organisations/${orgID}/csv_import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },
  exportProfilesCSV: (orgID: number): Promise<AxiosResponse<BlobPart>> => {
    return axiosApiService.get(
      `${BASE_URL}/api/organisations/${orgID}/profiles/export_csv`,
    );
  },
  showOrganisationSettings: (
    hashID: string,
  ): Promise<AxiosResponse<{ data: IOrganisationSettings }>> => {
    return axiosApiService.get(
      `${BASE_URL}/api/profiles/${hashID}/organisation_settings`,
    );
  },
  shareDetails: (
    hashID: string,
    body: {
      contact: {
        company_name: string;
        email: string;
        first_name: string;
        job_title: string;
        last_name: string;
        phone_number: string;
      };
    },
  ): Promise<AxiosResponse<void>> => {
    return axiosApiService.post(
      `${BASE_URL}/api/profiles/${hashID}/contacts`,
      body,
    );
  },
  viewSocialLink(hashID: string, event_type: string) {
    return axiosApiService.post(
      `${BASE_URL}/api/profiles/${hashID}/social_link`,
      { event_type: event_type || '' },
    );
  },
};

export default profilesAPI;
